import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  ChipProps,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  BriefingLineSyncState,
  BriefingMetricsRefreshResult,
  useGetBriefingMetricsStatusQuery,
  useRefreshBriefingMetricsMutation,
} from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { countMetricsStatus } from "../../hooks/useBriefingMetricsSync";

import OrbLoader from "../common/OrbLoader";
const STATE_COLORS: Record<BriefingLineSyncState, ChipProps["color"]> = {
  OK: "success",
  IN_PROGRESS: "info",
  PENDING_REPORT: "info",
  ERROR: "error",
  NO_DATA: "warning",
  NOT_CLOSED: "warning",
  STALE: "warning",
  NEVER_SYNCED: "warning",
  NOT_STARTED: "default",
  MISSING_DATES: "default",
  NOT_LINKED: "default",
};

// Orden en que se enseñan los contadores: primero lo que pide atención.
const SUMMARY_ORDER: BriefingLineSyncState[] = [
  "ERROR",
  "NO_DATA",
  "NOT_CLOSED",
  "STALE",
  "NEVER_SYNCED",
  "IN_PROGRESS",
  "PENDING_REPORT",
  "OK",
  "NOT_STARTED",
  "MISSING_DATES",
  "NOT_LINKED",
];

const formatDateTime = (iso?: string | null) =>
  iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "-";

// Las métricas se guardan por día a medianoche UTC: se enseña el día tal cual,
// sin pasarlo a hora local, que lo movería al día anterior al oeste de UTC.
const formatDay = (iso?: string | null) =>
  iso ? dayjs(iso.slice(0, 10)).format("DD/MM/YYYY") : "-";

interface BriefingMetricsSyncPanelProps {
  briefingId: string;
}

/**
 * Pestaña "Datos de plataforma": botón de "Actualizar datos" y estado de sincronización de
 * cada línea.
 *
 * Solo pinta. El sondeo mientras hay líneas actualizándose y la recarga de los reales al
 * terminar los lleva la página con `useBriefingMetricsSync`, porque esta pestaña se desmonta
 * al cambiar a otra y tienen que seguir funcionando. Lee la misma consulta, así que ve cada
 * respuesta del sondeo sin pedirla otra vez.
 */
const BriefingMetricsSyncPanel: React.FC<BriefingMetricsSyncPanelProps> = ({
  briefingId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { data, isLoading, isError } = useGetBriefingMetricsStatusQuery(
    { id: briefingId },
    { skip: !briefingId }
  );
  const [refreshMetrics, { isLoading: isRefreshing }] =
    useRefreshBriefingMetricsMutation();

  const status = data?.data;
  const summary = status?.summary;
  const { inProgress, problems, linkedLines } = countMetricsStatus(summary);

  const lines = useMemo(() => status?.lines ?? [], [status]);

  if (isLoading) {
    return (
      <Stack direction="row" justifyContent="center" py={6}>
        <OrbLoader size="large" />
      </Stack>
    );
  }

  if (isError || !status) {
    return (
      <Alert severity="error">
        {t(
          "briefingsView.metricsSync.loadError",
          "Unable to load the platform data status"
        )}
      </Alert>
    );
  }

  if (linkedLines === 0) {
    return (
      <Alert severity="info">
        {t(
          "briefingsView.metricsSync.noLinkedLines",
          "No line of this briefing is linked to a platform campaign yet, so there is no data to update."
        )}
      </Alert>
    );
  }

  const stateLabel = (state: BriefingLineSyncState) =>
    t(`briefingsView.metricsSync.states.${state}`, state);

  const describeResult = (result: BriefingMetricsRefreshResult) => {
    const parts = [
      t("briefingsView.metricsSync.queued", {
        count: result.queued,
        defaultValue: "{{count}} lines sent to update",
      }),
    ];
    if (result.skipped.length > 0) {
      parts.push(
        t("briefingsView.metricsSync.skipped", {
          count: result.skipped.length,
          defaultValue: "{{count}} skipped (recently requested or not started)",
        })
      );
    }
    if (result.failed.length > 0) {
      parts.push(
        t("briefingsView.metricsSync.failed", {
          count: result.failed.length,
          defaultValue: "{{count}} could not be sent",
        })
      );
    }
    return parts.join(" · ");
  };

  const handleRefresh = async () => {
    try {
      const response = await refreshMetrics({ id: briefingId }).unwrap();
      const result = response.data;
      if (result) {
        dispatch(
          setToastMessage({
            message: describeResult(result),
            severity: result.failed.length > 0 ? "warning" : "success",
          })
        );
      }
    } catch {
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.metricsSync.refreshError",
            "Could not request the data update"
          ),
          severity: "error",
        })
      );
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 3 },
        "@media print": { display: "none" },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t("briefingsView.metricsSync.title", "Platform data")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("briefingsView.metricsSync.checkedAt", {
              date: formatDateTime(status.checkedAt),
              defaultValue: "Checked at {{date}}",
            })}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          color="primary"
          onClick={handleRefresh}
          disabled={isRefreshing || inProgress === linkedLines}
          startIcon={
            isRefreshing || inProgress > 0 ? (
              <OrbLoader size="inline" state="connecting" />
            ) : (
              <SyncIcon />
            )
          }
          sx={{ whiteSpace: "nowrap" }}
        >
          {inProgress > 0
            ? t("briefingsView.metricsSync.updating", "Updating…")
            : t("briefingsView.metricsSync.refresh", "Update data")}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={2}>
        {SUMMARY_ORDER.filter((state) => (summary?.[state] ?? 0) > 0).map(
          (state) => (
            <Chip
              key={state}
              size="small"
              color={STATE_COLORS[state]}
              variant={
                STATE_COLORS[state] === "default" ? "outlined" : "filled"
              }
              label={`${stateLabel(state)}: ${summary?.[state]}`}
            />
          )
        )}
      </Stack>

      {problems > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t("briefingsView.metricsSync.problemsAlert", {
            count: problems,
            defaultValue:
              "{{count}} lines do not have up-to-date data. Check the detail and try updating them.",
          })}
        </Alert>
      )}

      <Box sx={{ overflowX: "auto", mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("briefingsView.metricsSync.columns.line", "Line")}
              </TableCell>
              <TableCell>
                {t("briefingsView.metricsSync.columns.state", "Status")}
              </TableCell>
              <TableCell>
                {t("briefingsView.metricsSync.columns.lastSync", "Last sync")}
              </TableCell>
              <TableCell>
                {t("briefingsView.metricsSync.columns.dataUntil", "Data until")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.associatedCampaignId}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {line.name}
                  </Typography>
                  {line.internalPlatform && (
                    <Typography variant="caption" color="text.secondary">
                      {line.internalPlatform}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      line.errorSync ??
                      t(`briefingsView.metricsSync.hints.${line.state}`, "")
                    }
                  >
                    <Chip
                      size="small"
                      color={STATE_COLORS[line.state]}
                      variant={
                        STATE_COLORS[line.state] === "default"
                          ? "outlined"
                          : "filled"
                      }
                      label={
                        line.state === "PENDING_REPORT" &&
                        line.pendingReports > 0
                          ? `${stateLabel(line.state)} (${line.pendingReports})`
                          : stateLabel(line.state)
                      }
                    />
                  </Tooltip>
                  {line.errorSync && (
                    <Typography
                      variant="caption"
                      color="error"
                      display="block"
                      sx={{ mt: 0.5, maxWidth: 360, wordBreak: "break-word" }}
                    >
                      {line.errorSync.length > 160
                        ? `${line.errorSync.slice(0, 160)}…`
                        : line.errorSync}
                    </Typography>
                  )}
                  {line.rateLimited && (
                    <Typography
                      variant="caption"
                      color="info.main"
                      display="block"
                      sx={{ mt: 0.5, maxWidth: 360 }}
                    >
                      {t(
                        "briefingsView.metricsSync.autoRetry",
                        "Platform request limit: retried automatically every 30 min (up to 4 times a day), otherwise in the nightly load."
                      )}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatDateTime(line.lastSyncAt)}</TableCell>
                <TableCell>{formatDay(line.lastMetricDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

export default BriefingMetricsSyncPanel;
